import React, { memo, useEffect, useState } from 'react';

import {
  Button,
  Modal,
  Input,
  Row,
  Col,
  Typography,
  Divider,
  Select,
} from 'antd';
import { SurveyType } from 'models/Survey';
import { useNavigate } from 'react-router-dom';
import { SurveyForm } from 'routes/Dashboard/CreateSurvey/CreateSurvey.props';
import { QUESTION_TYPE } from 'types/Enum';

import { Container, Section } from './CreateSurveyModal.style';

const { Title } = Typography;

export interface CreateSurveyModalProps {
  loading: boolean;
  onSubmit: (form: SurveyForm, title: string, order: string[]) => void;
  initialValues?: SurveyType;
}

const CreateSurveyModal = (props: CreateSurveyModalProps): JSX.Element => {
  // MARK:- Hooks
  const navigate = useNavigate();

  // MARK:- Refs

  // MARK:- State
  const [form, setForm] = useState<SurveyForm>({});
  const [title, setTitle] = useState<string>('');
  const [order, setOrder] = useState<string[]>([]);

  const [isAddingSection, setIsAddingSection] = useState(false);
  const [sectionName, setSectionName] = useState('');
  const [questionInput, setQuestionInput] = useState('');

  const [innerFormActive, setInnerFormActive] = useState('');

  // MARK:- Functions
  function goBack() {
    navigate('/dashboard/survey');
  }

  function onSubmitForm() {
    order.forEach((key) => {
      const newForm = { ...form };

      newForm[key].questions = newForm[key].questions.filter(
        (q) => q.question !== ''
      );

      setForm(newForm);
    });

    props.onSubmit(form, title, order);
  }

  function onClickAddSection() {
    setIsAddingSection(true);
    setQuestionInput('');
  }

  function onClickCancelAddSection() {
    setIsAddingSection(false);
    setQuestionInput('');
  }

  function addSection() {
    if (!form[sectionName]) {
      const newForm = { ...form };

      newForm[sectionName] = {
        questions: [],
      };

      setForm(newForm);
      setOrder([...order, sectionName]);
      setIsAddingSection(false);
      setSectionName('');
    }
  }

  function onClickAddQuestionToSection(key: string) {
    setInnerFormActive(key);
    setForm({
      ...form,
      [key]: {
        questions: [...form[key].questions, { question: '', type: QUESTION_TYPE.Points }],
      },
    });
  }

  function onAddQuestion(key: string) {
    const newQuestions = [...form[key].questions];

    const ndx = newQuestions.length - 1;

    newQuestions[ndx].question = questionInput;

    setForm({
      ...form,
      [key]: {
        questions: newQuestions,
      },
    });
    setInnerFormActive('');
    setQuestionInput('');
  }

  function onChangeSelect(key: string, value: QUESTION_TYPE) {
    const newQuestions = [...form[key].questions];

    const ndx = newQuestions.length - 1;
    newQuestions[ndx].type = value;

    setForm({
      ...form,
      [key]: {
        questions: newQuestions,
      },
    });
  }

  function onCancelAddQuestion(key: string) {
    const newQuestions = [...form[key].questions];
    newQuestions.pop();

    setForm({
      ...form,
      [key]: {
        questions: newQuestions,
      },
    });

    setInnerFormActive('');
    setQuestionInput('');
  }

  function editText(key: string, ndx: number) {
    // MARK:- Todo
  }

  // MARK:- Effects
  useEffect(() => {
    if (props.initialValues) {
      setForm({ ...props.initialValues.sections });
      setTitle(props.initialValues.title);
      setOrder(props.initialValues.order);
    }
  }, [props.initialValues]);

  // MARK:- Variables and Computed Values
  const formSectionsArr = Object.keys(form);

  const sectionsLength: Record<keyof typeof form, number> = {};

  formSectionsArr.forEach((key) => {
    sectionsLength[key] = form[key].questions.length;
  });

  return (
    <Container>
      <Modal
        title="Create Survey Form"
        centered
        visible
        onCancel={goBack}
        bodyStyle={{ minHeight: '500px' }}
        width="75%"
        footer={[
          <Button key="back" onClick={goBack} disabled={props.loading}>
            Return
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={props.loading}
            onClick={onSubmitForm}
          >
            Submit
          </Button>,
        ]}
      >
        <Col span={12} style={{ marginBottom: '8px' }}>
          <Input
            placeholder="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Col>

        {formSectionsArr.map((key, ndx) => {
          return (
            <>
              <Section key={key + ndx}>
                <Row justify="space-between">
                  <Title level={3}>{key}</Title>

                  {innerFormActive !== key && (
                    <Button
                      type="primary"
                      onClick={() => onClickAddQuestionToSection(key)}
                    >
                      Add Question To Section
                    </Button>
                  )}
                </Row>

                {
                  form[key].questions
                  .map((q, qndx) => (
                    <div
                      key={ndx+":"+qndx}
                      style={{
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'stretch'
                      }}
                    >
                      <div>{qndx + 1}</div>
                      <div>
                        {q.type}<br/>
                        {q.question}
                      </div>
                      <div>
                        <select
                          value={q.type}
                          defaultValue=""
                          onChange={event => {
                            const v: string = event.target.value;
                            q.type = QUESTION_TYPE[v as keyof typeof QUESTION_TYPE];
                          }}
                          >
                          <option value="" disabled>Choose one</option>
                          <option value="POINTS">Points</option>
                          <option value="INPUT">Input</option>
                          <option value="NUMERIC">Numeric</option>
                          <option value="MULTIPLE_INPUT">Multiple Input</option>
                          <option value="BOOLEAN">Boolean</option>
                        </select>
                      </div>
                      <div>
                        <textarea
                          rows={2}
                          cols={80}
                          value={q.question}
                          onChange={event => {
                            console.log(event.target.value);
                            q.question = event.target.value;
                          }}
                          />
                      </div>
                    </div>
                  ))
                }

                {innerFormActive === key && (
                  <Row gutter={8}>
                    <Col span={12}>
                      <Input
                        value={questionInput}
                        onChange={(e) => setQuestionInput(e.target.value)}
                      />
                    </Col>
                    <Col>
                      <Select
                        style={{ width: 120 }}
                        onChange={(value: QUESTION_TYPE) =>
                          onChangeSelect(key, value)
                        }
                        defaultValue={QUESTION_TYPE.Points}
                      >
                        <Select.Option value="POINTS">Points</Select.Option>
                        <Select.Option value="INPUT">Input</Select.Option>
                        <Select.Option value="NUMERIC">
                          Numeric / Percentage
                        </Select.Option>
                      </Select>
                    </Col>
                    <Col>
                      <Button type="primary" onClick={() => onAddQuestion(key)}>
                        Add
                      </Button>
                    </Col>
                    <Col>
                      <Button danger onClick={() => onCancelAddQuestion(key)}>
                        Cancel
                      </Button>
                    </Col>
                  </Row>
                )}
              </Section>

              <Divider />
            </>
          );
        })}

        {!isAddingSection ? (
          <Button type="primary" onClick={onClickAddSection}>
            Add Section
          </Button>
        ) : (
          <Row gutter={8}>
            <Col span={8}>
              <Input
                placeholder="Section Name"
                value={sectionName}
                onChange={(e) => setSectionName(e.target.value)}
              />
            </Col>
            <Col>
              <Button type="primary" onClick={addSection}>
                Add
              </Button>
            </Col>
            <Col>
              <Button
                danger
                onClick={onClickCancelAddSection}
                disabled={props.loading}
              >
                Cancel
              </Button>
            </Col>
          </Row>
        )}
      </Modal>
    </Container>
  );
};

export default memo(CreateSurveyModal);
